import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Box, Button, MenuItem, InputLabel, RadioGroup } from '@mui/material'

import A6PrintLabel1Page from '../../../assets/images/a6-print-label-1-page.svg'
import A4PrintLabel1Page from '../../../assets/images/a4-print-label-1-page.svg'
import A4PrintLabel4Page from '../../../assets/images/a4-print-label-4-page.svg'
import A4PrintLabel3Page from '../../../assets/images/a4-print-label-3-page.svg'
import PrintLabelFirstPosition from '../../../assets/images/print-label-first-position.svg'
import PrintLabelSecondPosition from '../../../assets/images/print-label-second-position.svg'
import PrintLabelThirdPosition from '../../../assets/images/print-label-third-position.svg'
import PrintLabelFourthPosition from '../../../assets/images/print-label-fourth-position.svg'
import PrintLabel3FirstPosition from '../../../assets/images/print-3-labels-first-position.svg'
import PrintLabel3SecondPosition from '../../../assets/images/print-3-labels-second-position.svg'
import PrintLabel3ThirdPosition from '../../../assets/images/print-3-labels-third-position.svg'
import useTranslations from '../../../i18n/useTranslations'
import Select from '../../../ui/components/select'
import { ReactComponent as ArrowsClockwise } from '../../../assets/icons/arrow-counter-clock-wise.svg'
import {
  MODEL_TYPE_NAMES,
  PRINT_POSITION_OPTIONS,
  PRINT_POSITION_TYPES,
} from '../../../AppProviders/PrinterProvider'

import RadioPreviewView from '../radioPreview'

import useStyles from './styles'

const MODEL_TYPE_IMAGES = {
  [MODEL_TYPE_NAMES.single]: A6PrintLabel1Page,
  [MODEL_TYPE_NAMES.multi1]: A4PrintLabel1Page,
  [MODEL_TYPE_NAMES.multi3]: A4PrintLabel3Page,
  [MODEL_TYPE_NAMES.multi4]: A4PrintLabel4Page,
  [`${MODEL_TYPE_NAMES.multi3}-position-1`]: PrintLabel3FirstPosition,
  [`${MODEL_TYPE_NAMES.multi3}-position-2`]: PrintLabel3SecondPosition,
  [`${MODEL_TYPE_NAMES.multi3}-position-3`]: PrintLabel3ThirdPosition,
  [`${MODEL_TYPE_NAMES.multi3}-position-4`]: A4PrintLabel3Page,
  [`${MODEL_TYPE_NAMES.multi4}-position-1`]: PrintLabelFirstPosition,
  [`${MODEL_TYPE_NAMES.multi4}-position-2`]: PrintLabelSecondPosition,
  [`${MODEL_TYPE_NAMES.multi4}-position-3`]: PrintLabelThirdPosition,
  [`${MODEL_TYPE_NAMES.multi4}-position-4`]: PrintLabelFourthPosition,
}

const calculateModelTypeImage = (modelType, printPosition) => {
  const hasPreviewWithoutSelectedPosition =
    printPosition === PRINT_POSITION_TYPES.askAlways ||
    [MODEL_TYPE_NAMES.single, MODEL_TYPE_NAMES.multi1].includes(modelType)

  return hasPreviewWithoutSelectedPosition
    ? MODEL_TYPE_IMAGES[modelType]
    : MODEL_TYPE_IMAGES[`${modelType}-position-${printPosition}`]
}

const Form = ({
  printers,
  modelTypes,
  printTypes,
  initialValues,
  onReloadPrinters,
  onPrinterChange,
  onModelTypeChange,
  onPrintTypeChange,
  onPrintPositionChange,
}) => {
  const t = useTranslations()
  const classes = useStyles()

  const [values, setValues] = useState({
    printer: '',
    modelType: '',
    printType: '',
    printPosition: '',
  })

  const handlePrinterChange = e => {
    const { value } = e.target

    setValues(values => ({
      ...values,
      printer: value,
    }))

    onPrinterChange(value)
  }

  const handleModelTypeChange = e => {
    const { value } = e.target

    setValues(values => ({
      ...values,
      modelType: value,
    }))

    onModelTypeChange(value)
  }

  const handlePrintTypeChange = e => {
    const { value } = e.target

    setValues(values => ({
      ...values,
      printType: value,
    }))

    onPrintTypeChange(value)
  }

  const handlePrintPositionChange = e => {
    const { value } = e.target

    setValues(values => ({
      ...values,
      printPosition: value,
    }))

    onPrintPositionChange(value)
  }

  useEffect(() => {
    if (initialValues) {
      setValues(initialValues)
    }
  }, [initialValues])

  return (
    <form>
      <Box className={classes.printersWrapper}>
        <Select
          label={t('print-settings:printer')}
          labelId="print-settings-printer"
          name="printer"
          value={values.printer}
          onChange={handlePrinterChange}
          className="input-large"
          isRequired
        >
          {printers.map(printer => (
            <MenuItem key={printer} value={printer}>
              {printer}
            </MenuItem>
          ))}
          {printers.length === 0 && (
            <MenuItem>{t('print-settings:no-printer')}</MenuItem>
          )}
        </Select>

        <Button
          size="small"
          variant="text"
          startIcon={<ArrowsClockwise />}
          onClick={onReloadPrinters}
          className={classes.reloadPrintersButton}
        >
          {t('print-settings:reload-printers')}
        </Button>
      </Box>

      <Box className={classes.printersModelWrapper}>
        <InputLabel required>{t('print-settings:print-model')}</InputLabel>
        <RadioGroup
          defaultValue={modelTypes[0].id}
          row
          sx={{ gap: 2, mt: 2 }}
          onChange={handleModelTypeChange}
          name="modelType"
          value={values.modelType}
        >
          {modelTypes.map(printSetting => (
            <RadioPreviewView
              value={printSetting}
              key={printSetting}
              label={t(`print-settings:model-${printSetting.toLowerCase()}`)}
              subLabel={t(
                `print-settings:model-${printSetting.toLowerCase()}-description`,
              )}
              image={calculateModelTypeImage(
                printSetting,
                values.printPosition,
              )}
            />
          ))}
        </RadioGroup>
      </Box>

      {PRINT_POSITION_OPTIONS[values.modelType] !== undefined ? (
        <Box className={classes.printPosition}>
          <Select
            label={t('print-modal:label-print-position')}
            labelId="print-settings-print-type"
            name="printPosition"
            value={values.printPosition}
            onChange={handlePrintPositionChange}
            className={classes.printTypeSelect}
            isRequired
          >
            {Object.values(PRINT_POSITION_OPTIONS[values.modelType]).map(
              printPosition => (
                <MenuItem key={printPosition} value={printPosition}>
                  {t(`print-settings:print-position:${printPosition}`)}
                </MenuItem>
              ),
            )}
          </Select>
        </Box>
      ) : null}

      <Select
        label={t('print-settings:print-type')}
        labelId="print-settings-print-type"
        name="printType"
        value={values.printType}
        onChange={handlePrintTypeChange}
        className={classes.printTypeSelect}
        isRequired
      >
        {printTypes.map(printType => (
          <MenuItem key={printType} value={printType}>
            {printType}
          </MenuItem>
        ))}
      </Select>
    </form>
  )
}

Form.propTypes = {
  printers: PropTypes.array.isRequired,
  modelTypes: PropTypes.array.isRequired,
  printTypes: PropTypes.array.isRequired,
  onReloadPrinters: PropTypes.func.isRequired,
  onPrinterChange: PropTypes.func.isRequired,
  onModelTypeChange: PropTypes.func.isRequired,
  onPrintTypeChange: PropTypes.func.isRequired,
  onPrintPositionChange: PropTypes.func.isRequired,
  initialValues: PropTypes.shape({
    printer: PropTypes.string,
    modelType: PropTypes.string,
    printType: PropTypes.string,
  }),
}

export default Form
